import {
  Activity,
  ActivityStatus,
  Document,
  TierIiReportFiledBy,
} from "generated-graphql/graphql";
import { useReport } from "./useReport";

export enum ActivityStepStatus {
  NotStarted,
  InProgress,
  Completed,
}

interface ActivityStep {
  status: ActivityStepStatus;
  title: string;
  timestamp?: string | undefined;
  documents: Array<Pick<Document, "id" | "title" | "documentType">>;
}

function getActivityStatus({
  activity,
  currentActivityId,
}: {
  activity: Pick<Activity, "status" | "id">;
  currentActivityId: string;
}) {
  // The ordering of these checks is important.
  // Something can be completed and current, but we want to show the completed.
  if (activity.status === ActivityStatus.Completed) {
    return ActivityStepStatus.Completed;
  }
  // Also, even if an activity is not started, if it's current,
  // we want to present it as in progress.
  if (activity.id === currentActivityId) {
    return ActivityStepStatus.InProgress;
  }
  return ActivityStepStatus.NotStarted;
}

const shouldRenderActivity = (activity: Pick<Activity, "displayName">) => {
  return activity.displayName !== null;
};

/**
 * This hook returns the steps of the activity stepper.
 */
export const useActivitySteps = (): ActivityStep[] | null => {
  const { data: report } = useReport();

  if (!report) return null;

  const reportIsFilingComplete =
    report.tierIIReport.encampStatus === "FILING_COMPLETE";

  // It's not just documents that don't have an activity, but,
  // we want all that are hidden too!
  const documentsRenderedUnderActivitiesIds = new Set<string>();

  const steps: ActivityStep[] =
    report.tierIIReport.currentWorkflow?.activities
      .filter(shouldRenderActivity)
      ?.map((activity) => {
        const status = getActivityStatus({
          activity,
          currentActivityId:
            report.tierIIReport.currentWorkflow?.currentActivityId ?? "",
        });
        const documentsToRenderUnderThisActivity =
          activity.documents
            ?.map((d) => d.document)
            // Make sure we don't double count documents that are rendered under multiple activities
            .filter((d) => !documentsRenderedUnderActivitiesIds.has(d.id)) ??
          [];

        // Add the documents to the set so we don't double count them later
        documentsToRenderUnderThisActivity.forEach((d) => {
          documentsRenderedUnderActivitiesIds.add(d.id);
        });

        return {
          status,
          title: activity.displayName ?? "",
          timestamp:
            activity.displayName?.trim().toLowerCase() === "filing complete" &&
            reportIsFilingComplete
              ? report.tierIIReport.filedOn || activity.updatedAt
              : activity.updatedAt,
          documents: documentsToRenderUnderThisActivity,
        };
      }) ?? [];

  const submittedStep = {
    status: ActivityStepStatus.Completed,
    title: `Report submitted${
      report.tierIIReport.filedBy === TierIiReportFiledBy.Encamp
        ? " by Encamp"
        : ""
    }`,
    timestamp:
      report.tierIIReport.filedBy === TierIiReportFiledBy.Encamp
        ? report.tierIIReport.verifiedAt
        : null,
    documents:
      report.tierIIReport.documents?.filter(
        (d) => !documentsRenderedUnderActivitiesIds.has(d.id)
      ) ?? [],
  };

  steps.unshift(submittedStep);

  const hasFilingCompleteStep = steps.some(
    (step) => step.title.trim().toLowerCase() === "filing complete"
  );

  if (!hasFilingCompleteStep) {
    steps.push({
      title: "Filing complete",
      status: reportIsFilingComplete
        ? ActivityStepStatus.Completed
        : ActivityStepStatus.NotStarted,
      timestamp:
        reportIsFilingComplete &&
        report.tierIIReport.filedBy === TierIiReportFiledBy.Encamp
          ? report.tierIIReport.filedOn
          : undefined,
      documents: [],
    });
  }

  // Ensure one step is in progress if the report isn't complete
  if (!reportIsFilingComplete) {
    const hasInProgress = steps.some(
      (step) => step.status === ActivityStepStatus.InProgress
    );
    if (!hasInProgress) {
      const firstNotStartedIdx = steps.findIndex(
        (step) => step.status === ActivityStepStatus.NotStarted
      );
      if (firstNotStartedIdx !== -1) {
        steps[firstNotStartedIdx].status = ActivityStepStatus.InProgress;
      }
    }
  }

  return steps;
};
